'use client';

import { ChevronRight } from '@/icons/ChevronRight';
import { HTTP_REQUEST } from '@/lib/Constants';
import { Endpoints } from '@/lib/Endpoints';
import { fetcher } from '@/lib/Requester';
import { cn } from '@/lib/utils';
import { Category } from '@/models/CategoryDetail';
import { PriceData, PriceDataContainer, Product } from '@/models/Product';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import OptimizedProductCard from './OptimizedProductCard';
import ProductListSwiper from './ProductListSwiper';
import VirtualizedProductList from './VirtualizedProductList';

export default function ProductListSection({
  products,
  category,
  title,
  url,
  slug,
  showMore,
  showTitle,
  enableSwiper,
  preventPriceFetch = true,
  fetchedPricesData,
  gridClassName,
  gridItemClassName,
  cardMaxWidth,
  cardClassName,
  cardImageClassName,
}: {
  products?: Product[];
  category?: Category;
  title: string;
  url: string;
  slug?: string;
  showMore: boolean;
  showTitle: boolean;
  enableSwiper?: boolean;
  preventPriceFetch?: boolean;
  fetchedPricesData?: PriceData | null;
  gridClassName?: string;
  gridItemClassName?: string;
  cardMaxWidth?: number;
  cardClassName?: string;
  cardImageClassName?: string;
}) {
  const [pricesData, setPricesData] = useState<PriceData | null>(null);

  useEffect(() => {
    if (preventPriceFetch) {
      if (fetchedPricesData) {
        setPricesData(fetchedPricesData);
        return;
      }

      const priceDataObj =
        products?.reduce((acc, product) => {
          acc[product.id] = {
            price: product.price,
            priceBeforeDiscount: product.priceBeforeDiscount,
            badge: product.badge,
          };
          return acc;
        }, {} as PriceData) || null;
      setPricesData(priceDataObj);
      return;
    }

    const getPriceData = async () => {
      try {
        const response = await fetcher<PriceDataContainer>({
          params: {},
          cacheKey: '',
          endpoint: Endpoints.PRICES,
          method: HTTP_REQUEST.GET,
          cacheEnabled: false,
        });
        setPricesData(response?.items);
      } catch {
        setPricesData(null);
      }
    };

    getPriceData();
  }, [products, preventPriceFetch, fetchedPricesData]);

  const sectionId = `product-section-${slug || title?.replace(/\s+/g, '-').toLowerCase()}`;

  const shouldUseVirtualization = products && products.length > 120000;

  return (
    <section className="virtualized-product-list">
      <div className="px-4 md:px-0 flex justify-between items-center">
        {showTitle && (
          <h2
            id={sectionId}
            className="text-hzc_purple_500 font-bold text-xl sm:text-2xl md:text-3xl xl:text-4xl 2xl:text-[44px]"
          >
            {title}
          </h2>
        )}
        {showMore ? (
          <Link
            href={url}
            className="justify-center gap-2 my-auto flex group"
            aria-label={`${title} ürünlerinin tamamını gör`}
          >
            <span className="text-hzc_purple_500 font-medium text-sm">Tümünü Gör</span>
            <ChevronRight className="text-sm my-auto group-hover:translate-x-2 transition-all duration-300" />
          </Link>
        ) : null}
      </div>

      {enableSwiper ? (
        <ProductListSwiper
          products={products}
          priceData={pricesData}
          slug={slug || url.split('/').pop() || ''}
          title={title}
        />
      ) : shouldUseVirtualization ? (
        <VirtualizedProductList
          products={products}
          title={title}
          url={url}
          slug={slug}
          showMore={showMore}
          showTitle={false} // Already shown above
          priceData={pricesData}
          itemsPerPage={12}
        />
      ) : (
        <div className={cn('product-grid !overflow-visible', gridClassName)}>
          {products?.map((product, index) => (
            <div key={product.id} className={cn('product-grid-item', gridItemClassName)}>
              <OptimizedProductCard
                product={product}
                category={category}
                categoryName={title}
                // priority={index < 10}
                priority={false}
                price={pricesData?.[product.id]}
                listId={slug || ''}
                listName={title}
                className={cn('mb-4', cardClassName)} // Add consistent margin bottom
                maxWidth={cardMaxWidth}
                imageClassName={cardImageClassName}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
